import Link from 'next/link';
import React, { memo, useEffect, useMemo, useState } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import HomeIcon from '@mui/icons-material/Home';
import { Paper } from '@mui/material';

import { PageTypes } from '@interfaces/pageType';
import { Layouts } from '@interfaces/layouts';
import { DashboardFilters } from '@interfaces/dashboard';
import {
  BOT_FUNNEL_STAGE_LABELS,
  BotFunnelStage,
} from '@interfaces/reports/botFunnel';
import {
  SalesSummary,
  SalesSummaryFilters,
} from '@interfaces/reports/salesSummary';

import { findSalesSummary } from '@api/reports';
import { parseFilters } from '@utils/parseFilters';
import parseDate from '@utils/parseDate';
import useToastContext from '@contexts/ToastContext/hook';

import { Typography } from '@vibTheme/components/_base/Typography';
import VibBreadcrumbs, {
  BreadcrumbItem,
} from '@vibTheme/components/VibBreadcrumbs';
import { Alert } from '@vibTheme/components/VibAlert';
import { SemPapelDeComissao } from '@components/Dashboard/SemPapelDeComissao';
import { useTemPapelDeComissao } from '@hooks/useTemPapelDeComissao';
import { useComunidadesQueOPainelServe } from '@hooks/useComunidadesQueOPainelServe';
import {
  familiasDoPainel,
  ROTULO_DA_FAMILIA,
  FamiliaDoPainel,
} from '@utils/familiaDoPainel';
import useGroupsContext from '@contexts/GroupsContext/hook';
import { VibAccordion } from '@vibTheme/components/VibAccordion';
import { VibLinearProgress } from '@vibTheme/components/VibLinearProgress';
import { ConversionFilters } from '@components/Dashboard/ConversionFilters';
import { DashboardVersionSwitch } from '@components/Dashboard/DashboardVersionSwitch';
import { NPSBanner } from '@components/NPSBanner/NPSBanner';
import { DiscoveryCard } from '@components/Discovery/DiscoveryCard';
import { useDiscoveryCard } from '@hooks/useDiscoveryCard';
import { pickDiscoveryCard } from '@utils/pickDiscoveryCard';
import { PaymentMethods } from '@utils/parsePaymentMethodName';
import parseMoneyValue from '@utils/parseMoneyValue';
import { DashboardVersions } from '@interfaces/dashboardVersion';
import { useDashboardVersion } from '@hooks/useDashboardVersion';
import { DashboardCard } from '@vibTheme/components/VibCharts';
import {
  hasSubscriptionBase,
  showsBaseMovement,
} from '@utils/hasSubscriptionBase';
import {
  describePopulation,
  resolvePopulation,
} from '@utils/resolvePopulation';

import { SalesChart } from '@vibTheme/components/VibCharts/SalesChart';
import { SalesHistoryChart } from '@vibTheme/components/VibCharts/SalesHistoryChart';
import { GroupMetricsCharts } from '@vibTheme/components/VibCharts/GroupMetricsCharts';
import { ProfitIndicator } from '@vibTheme/components/VibCharts/ProfitIndicator';
import { CustomHelpTooltip } from '@vibTheme/components/VibCharts/CustomHelpTooltip';
import {
  MetricCard,
  formatMinutes,
  formatRate,
} from '@vibTheme/components/VibCharts/PaymentConversion/shared';
import {
  Cell,
  ReportTable,
  Row,
} from '@vibTheme/components/VibCharts/reportTable';
import {
  formatDays,
  formatDuration,
  formatMoney,
  orDash,
} from '@vibTheme/components/VibCharts/reportFormat';
import { useCustomerValue } from '@vibTheme/components/VibCharts/CustomerValue/useCustomerValue';
import { userRolesDoPedido } from '@utils/userRolesDoPedido';
import { useTimeToConvert } from '@vibTheme/components/VibCharts/TimeToConvert/useTimeToConvert';
import { useRemarketingAttribution } from '@vibTheme/components/VibCharts/RemarketingAttribution/useRemarketingAttribution';
import {
  MrrCoverageNote,
  RecurringRevenueTable,
  describeAtRiskMrr,
  formatCoveredMoney,
  formatSignedMoney,
} from '@vibTheme/components/VibCharts/RecurringRevenue/shared';
import { useRecurringRevenue } from '@vibTheme/components/VibCharts/RecurringRevenue/useRecurringRevenue';
import { usePaymentConversion } from '@vibTheme/components/VibCharts/PaymentConversion/usePaymentConversion';
import { useBotFunnel } from '@vibTheme/components/VibCharts/BotFunnel/useBotFunnel';
import { ReportUnavailable } from '@vibTheme/components/VibCharts/ReportUnavailable';
import { reportLinkHref } from '@utils/reportLinkParams';
import {
  EmptyFunnel,
  FunnelHelpTooltip,
  formatRate as formatFunnelRate,
  OfferBreakdown,
  OriginBreakdown,
  PaymentMethodFunnelBreakdown,
  StageBreakdown,
  describeUncovered,
  formatDay,
  formatSalesRatio,
} from '@vibTheme/components/VibCharts/BotFunnel/shared';

/** Um namespace para toda a família de telas: o recorte escolhido aqui é o que Recorrência abre. */
const SELECTED_FILTERS_PAGE = 'dashboard';

/** Abaixo disso a comparação é ruído: com 3 vendas contra 1, "+200%" é aritmética verdadeira e medida falsa. */
const MIN_SAMPLE_FOR_DELTA = 30;

/** O dobro do vão de dentro do grupo: é o que separa um assunto do outro sem moldura. */
const GROUP_GAP = 'pb-2xl';

/** Quatro das dez etapas: as outras seis são diagnóstico, não decisão. */
const MACRO_STAGES: { label: string; stage: BotFunnelStage }[] = [
  // ATTEMPT_OPENED e não START: quem chega por link direto nunca envia /start, e a base ficava menor que as etapas.
  { label: 'Iniciou uma conversa', stage: 'ATTEMPT_OPENED' },
  { label: 'Escolheu uma oferta', stage: 'OFFER_SELECTED' },
  { label: 'Gerou a cobrança', stage: 'INVOICE_CREATED' },
];

/** Só as etapas que ninguém alcança sem a anterior: nas puláveis, a diferença lida como perda acusa falso. */
const SEQUENTIAL_STAGES: BotFunnelStage[] = [
  // START fica fora: é subconjunto de quem iniciou conversa, e virava a maior queda do funil.
  'ATTEMPT_OPENED',
  'OFFERS_SHOWN',
  'OFFER_SELECTED',
  'PAYMENT_METHOD_SELECTED',
  'INVOICE_CREATED',
];

/**
 * Sem comunidade selecionada não há link: o destino abriria sobre outro conjunto que não o dos cards zerados
 * ao lado.
 */
/*
  Exportado para teste: a regra de quando NÃO há link é a parte que importa, e ela é uma expressão
  pura — testá-la pela tela exigiria montar o corpo novo e dublar os dez módulos de gráfico dele.
*/
export const reportHref = (path: string, filters: DashboardFilters) =>
  /*
    Sem PAPEL não há link, e é o mesmo motivo de não haver sem comunidade: o destino não sabe
    responder pelo recorte que gerou o número desta tela.

    Os cinco detalhes pedem papel — eles não recebem `admiteSemPapel`. Um link sem papel leva o
    colaborador puro à tela de indisponível, e leva quem tem papel em OUTRA comunidade a passar pelo
    portão, cair no papel de partida, recusar os ids entregues e abrir silenciosamente uma comunidade
    diferente. Nenhum link é melhor que link que muda de assunto.

    Volta quando os detalhes souberem servir o modo — é o que a #360 deixa registrado.
  */
  filters.botConfigIds.length === 0 || !filters.userRole
    ? undefined
    : reportLinkHref(path, {
        startDate: filters.startDate,
        endDate: filters.endDate,
        previousPeriodStartDate: filters.previousPeriodStartDate,
        previousPeriodEndDate: filters.previousPeriodEndDate,
        userRoles: userRolesDoPedido(filters.userRole),
        botConfigIds: filters.botConfigIds,
      });

/**
 * A home da Área do Criador: uma rota, duas versões do dashboard.
 *
 * Uma rota só porque duas obrigariam a escolher entre a versão nova num endereço de segunda classe ou o
 * endereço salvo do produtor mudando debaixo dele. Cabeçalho, breadcrumb, NPS e filtros são os mesmos; troca
 * o corpo, e a seleção sobrevive à troca por ser o mesmo estado.
 *
 * A preferência vem de `platform_user` pela sessão, e cada troca grava. Quem nunca escolheu recebe a nova.
 *
 * Um bloco por domínio, com dois ou três números e o link para o relatório: número único fica aqui, tabela
 * que se fatia vai para o relatório. Bloco que não se aplica desaparece em vez de mostrar zero.
 */
export default function Dashboard() {
  const [filters, setFilters] = useState<DashboardFilters | null>(null);
  const { version, chooseVersion } = useDashboardVersion();
  const { escopoPorProduto } = useGroupsContext();

  /*
    DUAS perguntas, e não uma: quem cada versão do painel sabe servir.

    O corpo legado trata qualquer papel diferente de `AFFILIATED` como produtor e não particiona
    nada, então ele só serve quem tem papel de comissão. O corpo novo monta uma família por escopo
    resolvido, e a família de produtor serve também a comunidade alcançada por grant — que é quem a
    #360 existe para atender.
  */
  /*
    Uma resposta, três consumidores: o portão, a lista do seletor e a partição em famílias. Divergir
    entre eles é o que produz "tela aberta e vazia" de três jeitos diferentes.
  */
  const comunidadesServidas = useComunidadesQueOPainelServe();

  const podeUsarOLegado = useTemPapelDeComissao(false);
  const podeUsarONovo =
    useTemPapelDeComissao(true) && escopoPorProduto.length > 0;

  /*
    Quem o legado não serve não escolhe o legado, e a razão é uma armadilha e não estética.

    `chooseVersion` PERSISTE a escolha — sessão e perfil. Se a pessoa que só o novo serve escolhesse
    a versão anterior, o portão do legado fecharia, a tela viraria só o aviso, e o interruptor de
    versão iria embora junto: sem caminho de volta, e a preferência gravada faria isso valer nas
    visitas seguintes. Trancada fora por uma escolha reversível que deixou de ser reversível.
  */
  const versaoEfetiva = podeUsarOLegado ? version : DashboardVersions.NEW;

  /*
    A capacidade de servir a AUSÊNCIA de papel, que é o modo "Tudo o que eu alcanço".

    Só o corpo novo a tem, e só com o descritor presente — sem ele `familiasDoPainel` não tem por
    onde particionar. É a mesma expressão que o portão recebe: portão e filtro discordando dá portão
    aberto com filtro pedindo papel que a pessoa não tem.
  */
  const admiteSemPapel =
    versaoEfetiva === DashboardVersions.NEW && podeUsarONovo;

  const temPapelDeComissao = podeUsarONovo || podeUsarOLegado;

  if (!temPapelDeComissao)
    return (
      <section>
        <VibBreadcrumbs items={breadcrumbs} />
        <Typography component="h1" className="mb-main">
          Dashboard
        </Typography>
        <SemPapelDeComissao oQue="O painel" />
      </section>
    );

  return (
    <section>
      <NPSBanner />
      <VibBreadcrumbs items={breadcrumbs} />

      {/*
        O interruptor só aparece para quem tem as DUAS versões. Oferecer a que não serve é o que
        criava a armadilha: escolha persistida, portão fechado, interruptor fora da tela.
      */}
      {podeUsarOLegado && (
        <DashboardVersionSwitch
          version={versaoEfetiva}
          onChange={chooseVersion}
        />
      )}

      <div className="mb-main sm:flex sm:gap-main sm:items-center md:justify-between">
        <Typography component="h1" className="mb-main sm:mb-0">
          Dashboard
        </Typography>

        {/*
          A ausência de papel só é OFERECIDA onde é servida, e são duas condições:

          - a versão NOVA do painel, que é a única que particiona a seleção por escopo resolvido; o
            painel legado trata tudo que não é `AFFILIATED` como produtor e mandaria ids mistos para
            widgets de produtor;
          - e o descritor presente, porque sem ele `familiasDoPainel` não tem por onde particionar e
            devolveria lista vazia — painel em branco, que é pior que o comportamento antigo.

          A segunda condição é a janela de deploy da api#277. As duas juntas: oferecer a opção onde
          ela não é servida troca tela que explica por toast de erro.
        */}
        <ConversionFilters
          /*
            Namespace COMPARTILHADO com os cinco detalhes, e é de propósito: o detalhe é um
            aprofundamento do painel, e as comunidades que a pessoa escolheu são o escopo que ela
            espera manter.

            Tive um namespace separado para o modo sem papel, como isolamento — e ele resolvia um
            problema que já está resolvido melhor: a seleção lembrada e o papel lembrado passaram a
            ser VALIDADOS contra a elegibilidade de quem lê, no componente compartilhado. Com isso,
            uma seleção do modo sem papel chegando a um consumidor que pede papel é podada em vez de
            enviada crua.

            Separar cobrava um preço que o isolamento não valia: mudar comunidade num detalhe deixava
            de voltar para o painel, e trocar de versão perdia uma seleção que a outra versão sabia
            ler.
          */
          selectedFiltersPage={SELECTED_FILTERS_PAGE}
          onChange={setFilters}
          /*
            `key` pela capacidade: trocar de versão do painel REMONTA os filtros, e é o que recalcula
            o papel de partida. Sem remontar, o campo só seria corrigido por efeito passivo do
            `InputSelect`, depois de o corpo legado já ter pedido.
          */
          /*
            `key` pela capacidade: trocar de versão do painel REMONTA os filtros, e é o que recalcula
            o papel de partida. Sem remontar, o campo só seria corrigido por efeito passivo do
            `InputSelect`, depois de o corpo legado já ter pedido.
          */
          key={admiteSemPapel ? 'sem-papel' : 'por-papel'}
          admiteSemPapel={admiteSemPapel}
          comunidadesServidas={comunidadesServidas}
        />
      </div>

      {filters &&
        (versaoEfetiva === DashboardVersions.PREVIOUS ? (
          /*
            O corpo LEGADO só monta COM papel no filtro, e o remonte acima não basta: a seleção de
            comunidades só volta pelo `onChange` depois do debounce de 500ms, então o legado pediria
            primeiro, com o estado do modo sem papel — ids possivelmente mistos e papel ausente, que
            ele lê como produtor.

            Esperar é o desfecho certo, e é o mesmo que a tela já faz enquanto os filtros não
            chegaram: `filters` nulo também não monta corpo nenhum.
          */
          filters.userRole && (
            <MemoizedDashboardCard dashBoardFilters={filters} />
          )
        ) : (
          <NewVersionBody filters={filters} />
        ))}
    </section>
  );
}

/** Memoizada por JSON e não por identidade: ConversionFilters entrega um objeto novo a cada notificação. */
const MemoizedDashboardCard = memo(
  DashboardCard,
  (prev, next) => JSON.stringify(prev) === JSON.stringify(next),
);

const breadcrumbs: BreadcrumbItem[] = [
  {
    label: 'Dashboard',
    icon: <HomeIcon />,
  },
];

Dashboard.pageType = PageTypes.PRIVATE;
Dashboard.layout = Layouts.DEFAULT;

/**
 * Componente próprio para não montar na versão anterior: nenhum dos seis hooks de relatório chega a pedir
 * nada. Também garante filtros resolvidos antes do primeiro disparo.
 */
/*
  A prop entra como `filtersDaTela` e o corpo segue usando `filters`.

  É de propósito: o resto desta função — e tudo que ela passa adiante — pergunta por
  `filters.botConfigIds`, e o que mudou é justamente QUAIS ids são esses. Renomear a entrada faz o
  compilador garantir que nada aqui volte a ler a seleção crua sem passar pelo recorte da família.
*/
function NewVersionBody({
  filters: filtersDaTela,
}: {
  filters: DashboardFilters;
}) {
  const { escopoPorProduto } = useGroupsContext();

  /*
    As FAMÍLIAS de blocos e as comunidades de cada uma.

    O painel decidia pelo papel único da seleção, e é o que impedia a seleção mista (#360): a
    comunidade alcançada por grant não é de afiliado, e tratar tudo como produtor entregaria o
    faturamento cheio de uma comunidade onde a pessoa só tem fatia.

    A regra vive em `familiasDoPainel` porque a ORDEM entre papel escolhido e descritor é sutil e tem
    caso de teste próprio — inverter põe números de dois escopos na mesma tela.
  */
  /*
    Os filtros de cada família saem do MESMO `useMemo` da decisão, e não de um por família.

    A primeira versão memoizava dentro do `.map` — hook em laço, que o React proíbe: a contagem de
    hooks passaria a variar com a quantidade de famílias. Derivar tudo aqui dá objetos estáveis (os
    gráficos dependem do objeto inteiro e refariam a requisição a cada render com literal novo) sem
    hook nenhum dentro da iteração.
  */
  const familias = useMemo(
    () =>
      familiasDoPainel({
        papelEscolhido: filtersDaTela.userRole,
        botConfigIds: filtersDaTela.botConfigIds,
        escopos: escopoPorProduto,
      }).map((entrada) => ({
        ...entrada,
        filters: { ...filtersDaTela, botConfigIds: entrada.botConfigIds },
      })),
    [filtersDaTela, escopoPorProduto],
  );

  /*
    Cabeçalho SÓ quando há mais de uma família na tela.

    Com uma só ele é ruído: a tela inteira fala daquela família, e é o caso de todo mundo que escolhe
    um papel no filtro. Com duas ele é o que impede a pessoa de ler dois blocos de vendas com números
    diferentes sem saber qual é qual (#367).
  */
  const nomeia = familias.length > 1;

  return (
    <>
      {familias.map(({ familia, botConfigIds, filters }) => (
        <CorpoDaFamilia
          /*
            `key` pela família E pelas comunidades: trocar a seleção remonta o corpo, que é o que os
            gráficos já pediam por dentro — eles commitam toda resposta sem guarda de requisição
            superada, então resposta atrasada cairia em instância desmontada.
          */
          key={`${familia}|${botConfigIds.join(',')}`}
          familia={familia}
          nomeia={nomeia}
          filters={filters}
        />
      ))}
    </>
  );
}

/**
 * O corpo de UMA família: os dados dela e os blocos que ela serve.
 *
 * Era o corpo do `NewVersionBody`, e virou componente para poder ser montado uma vez por família.
 * É o que resolve o impedimento que o código antigo registrava — "hook não pode ser condicional,
 * então esconder a saída deixaria a requisição sair": a condição virou MONTAR o corpo, e família sem
 * comunidade simplesmente não é montada, então hook nenhum dela roda.
 */
function CorpoDaFamilia({
  filters,
  familia,
  nomeia,
}: {
  filters: DashboardFilters;
  familia: FamiliaDoPainel['familia'];
  nomeia: boolean;
}) {
  const isAffiliate = familia === 'afiliado';

  const salesFilters = useMemo<SalesSummaryFilters>(
    () => ({
      botConfigIds: filters.botConfigIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
      previousPeriodStartDate: filters.previousPeriodStartDate,
      previousPeriodEndDate: filters.previousPeriodEndDate,
      clientTz: filters.clientTz,
      userRoles: userRolesDoPedido(filters.userRole),
    }),
    [filters],
  );

  // Literal novo a cada render faria os gráficos, que dependem do objeto inteiro, refazer a requisição.
  // Aqui em cima porque valor-do-cliente é o único bloco que serve aos dois ramos.
  const reportFilters = useMemo(
    () => ({
      botConfigIds: filters.botConfigIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
      clientTz: filters.clientTz,
      userRoles: userRolesDoPedido(filters.userRole),
    }),
    [filters],
  );

  /** Fora dos ramos porque a report-api atende o afiliado aqui, diferente dos outros relatórios. */
  const customer = useCustomerValue(reportFilters);

  const historyFilters = useMemo(
    () => ({
      botConfigIds: filters.botConfigIds,
      clientTz: filters.clientTz,
      userRoles: userRolesDoPedido(filters.userRole),
    }),
    [filters],
  );

  const groupFilters = useMemo(
    () => ({
      botConfigIds: filters.botConfigIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
    }),
    [filters],
  );

  /**
   * SalesHistoryChart e GroupMetricsCharts commitam toda resposta sem guarda de requisição superada, então
   * remontar por seleção faz a resposta atrasada cair em instância desmontada. Cada chave lista só o que o
   * gráfico consome.
   */
  const historyKey = useMemo(
    () =>
      `${filters.botConfigIds.join(',')}|${filters.userRole}|${
        filters.clientTz
      }`,
    [filters],
  );

  const groupKey = useMemo(
    () =>
      `${filters.botConfigIds.join(
        ',',
      )}|${filters.startDate.getTime()}|${filters.endDate.getTime()}`,
    [filters],
  );

  const sales = useSalesSummaryPair(salesFilters);
  const priorSalesCount = Number(sales.previous?.salesTotalCount ?? 0);

  // Slot porque a posição do histórico difere entre os dois ramos.
  const historySlot = (
    <Paper className="mt-2xl">
      <Typography component="h3" className="p-main">
        Histórico de Vendas
      </Typography>
      <SalesHistoryChart key={historyKey} filters={historyFilters} />
    </Paper>
  );

  const groupSlot = (
    <GroupMetricsCharts key={groupKey} filters={groupFilters} />
  );

  return (
    <section>
      {nomeia && (
        <header className="mt-2xl">
          <Typography component="h2">
            {ROTULO_DA_FAMILIA[familia].titulo}
          </Typography>
          <Typography className="text-sm text-lightBlack">
            {ROTULO_DA_FAMILIA[familia].explicacao}
          </Typography>
        </header>
      )}

      {isAffiliate ? (
        <AffiliateSections
          filters={filters}
          sales={sales}
          priorSalesCount={priorSalesCount}
          historySlot={historySlot}
          customer={customer}
          customerHref={reportHref('/reports/valor-do-cliente', filters)}
        />
      ) : (
        <ProducerSections
          filters={filters}
          sales={sales}
          priorSalesCount={priorSalesCount}
          historySlot={historySlot}
          groupSlot={groupSlot}
          customer={customer}
        />
      )}
    </section>
  );
}

function AffiliateSections({
  filters,
  sales,
  priorSalesCount,
  historySlot,
  customer,
  customerHref,
}: {
  filters: DashboardFilters;
  sales: ReturnType<typeof useSalesSummaryPair>;
  priorSalesCount: number;
  historySlot: React.ReactNode;
  customer: ReturnType<typeof useCustomerValue>;
  customerHref?: string;
}) {
  return (
    <>
      {/* Sem o grupo "A base hoje": o afiliado ganha sobre faturas, não sobre base de assinantes. */}
      <section className={GROUP_GAP}>
        <GroupLabel
          title="No período"
          subtitle={`de ${parseDate(filters.startDate, {
            type: 'date',
          })} a ${parseDate(filters.endDate, { type: 'date' })}`}
        />
        <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-3">
          <SalesCards sales={sales} priorSalesCount={priorSalesCount} />
          <RefundCard sales={sales} priorSalesCount={priorSalesCount} />
        </div>
      </section>

      <Alert variant="info">
        <Typography className="text-sm">
          Funil do bot e conversão de pagamento não se aplicam ao papel de
          afiliado. A comissão só existe depois da cobrança paga, então o
          denominador dessas taxas não existe para você.
        </Typography>
      </Alert>

      {/* Único relatório desta tela que a report-api atende para o afiliado. */}
      <CustomerValueBlock customer={customer} href={customerHref} />

      {historySlot}
    </>
  );
}

/** Existe para os hooks restritos a produtor só montarem quando o papel tem direito aos dados. */
function ProducerSections({
  filters,
  sales,
  priorSalesCount,
  historySlot,
  groupSlot,
  customer,
}: {
  filters: DashboardFilters;
  sales: ReturnType<typeof useSalesSummaryPair>;
  priorSalesCount: number;
  historySlot: React.ReactNode;
  groupSlot: React.ReactNode;
  customer: ReturnType<typeof useCustomerValue>;
}) {
  const conversionFilters = useMemo(
    () => ({
      botConfigIds: filters.botConfigIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
      previousPeriodStartDate: filters.previousPeriodStartDate,
      previousPeriodEndDate: filters.previousPeriodEndDate,
      clientTz: filters.clientTz,
      userRoles: userRolesDoPedido(filters.userRole),
    }),
    [filters],
  );

  const funnelFilters = useMemo(
    () => ({
      botConfigIds: filters.botConfigIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
      clientTz: filters.clientTz,
    }),
    [filters],
  );

  const recurringFilters = useMemo(
    () => ({
      botConfigIds: filters.botConfigIds,
      startDate: filters.startDate,
      endDate: filters.endDate,
      clientTz: filters.clientTz,
      userRoles: userRolesDoPedido(filters.userRole),
    }),
    [filters],
  );

  const conversion = usePaymentConversion(conversionFilters);
  const funnel = useBotFunnel(funnelFilters);
  const recurring = useRecurringRevenue(recurringFilters);
  const timing = useTimeToConvert(recurringFilters);
  const remarketing = useRemarketingAttribution(recurringFilters);

  // useBotFunnel mantém a resposta anterior em voo: sem `isLoading`, a tabela mostra o período antigo.
  const isFunnelReady = !funnel.isLoading && funnel.hasData;

  /**
   * `totals` soma todos os meios de pagamento, e incluiria cartão recusado — o downsell é sobre Pix
   * abandonado, então isola as linhas de `conversion.data.rows` (por período E por meio de pagamento)
   * antes de somar.
   */
  const pixAbandonado = useMemo(() => {
    const linhas = conversion.data.rows.filter(
      (row) => row.paymentMethod === PaymentMethods.PIX,
    );

    return linhas.reduce(
      (soma, row) => ({
        cobrancas: soma.cobrancas + (row.createdCount - row.paidCount),
        valor: soma.valor + (row.createdGrossValue - row.paidGrossValue),
      }),
      { cobrancas: 0, valor: 0 },
    );
  }, [conversion.data.rows]);

  /**
   * Quem conversou com o bot e não chegou a gerar cobrança — parte da audiência que a recuperação
   * alcança, e a parte que dá para contar com exatidão.
   *
   * **Por que não `leads - purchasers`:** `purchasers` conta quem comprou NO período e `leads` quem abriu
   * conversa no período. Quem comprou tendo conversado antes entra num e não no outro, então a subtração
   * mistura duas coortes e o resultado não é "quantos da coorte não compraram" — erra para baixo, e
   * `Math.max` esconderia só o caso extremo. A etapa do funil, ao contrário, é medida A PARTIR DO TOPO
   * (é o que `rateFromTop` significa), então quem gerou cobrança é subconjunto de quem conversou e a
   * subtração fecha.
   *
   * O recorte fica mais estreito que a audiência real do remarketing — o bot só para a sequência quando a
   * pessoa PAGA, então quem gerou Pix e não pagou também recebe. Ficar de fora é de propósito: esse grupo
   * é o número do card do downsell, e dois cards anunciando as mesmas pessoas competiriam pelo mesmo
   * problema.
   */
  const etapaCobranca = funnel.data.stages.find(
    (stage) => stage.stage === 'INVOICE_CREATED',
  );

  /*
    Etapa ausente vale ZERO, não "sem dado": o funil omite a linha quando ninguém chegou nela, e é assim
    que o `FunnelSection` logo abaixo já lê (`leadsByStage[stage] ?? 0`). Tratar a ausência como falta de
    dado esconderia o card exatamente da comunidade onde todo lead abandonou antes de gerar cobrança — a
    que mais precisa da recuperação.

    Quem responde por dado que não chegou é o gate de cobertura, não esta subtração.
  */
  const semCobranca = Math.max(
    0,
    funnel.data.attemptsOpened.leads - (etapaCobranca?.leads ?? 0),
  );

  const {
    show: showRemarketingCard,
    isPending: remarketingPending,
    productCode: remarketingProductCode,
    dismiss: dismissRemarketing,
  } = useDiscoveryCard({
    feature: 'remarketing',
    toggle: 'remarketing',
    permission: 'remarketing',
    selectedIds: filters.botConfigIds,
    /*
      Cobertura tem três estados, e `hasData` aceita dois: `isMeasured` só exclui `unmeasured`, então
      medição PARCIAL passa por ele. Um número que cobre fração do período, com a cópia dizendo "no
      período selecionado", é exatamente a mentira que `useBotFunnel` documenta — por isso o gate exige
      `full`, e não `isFunnelReady`.

      Cobertura é o único gate de "dado que não chegou". Linha de etapa ausente NÃO é esse caso: ver o
      comentário de `semCobranca`.
    */
    hasSales:
      !funnel.isLoading && funnel.coverage.kind === 'full' && semCobranca > 0,
  });

  const {
    show: showScheduleCard,
    isPending: schedulePending,
    dismiss: dismissSchedule,
    scheduleEvidence,
  } = useDiscoveryCard({
    feature: 'scheduleMessages',
    toggle: 'scheduleMessages',
    permission: 'scheduleMessages',
    // A tela de envio esconde a ação de quem só tem leitura.
    action: 'create',
    selectedIds: filters.botConfigIds,
    /*
      Sem contato não há a quem enviar, e o gate de "tem audiência" mora no hook porque
      é ele que sabe quais comunidades o card representa depois de filtrar as atrasadas.
    */
    hasSales: true,
  });

  // Sem condicional: ProducerSections só tem returns no fim, e hook não pode nascer atrás de um `if`.
  const {
    show: showDownsellCard,
    isPending: downsellPending,
    productCode: downsellProductCode,
    dismiss: dismissDownsell,
  } = useDiscoveryCard({
    feature: 'downsell',
    toggle: 'downsell',
    permission: 'coupons',
    // O CTA abre /downsells/create, que exige canCreate.
    action: 'create',
    selectedIds: filters.botConfigIds,
    /*
      O próprio bloco é a evidência: recorte sem Pix abandonado não convida a nada.

      `!conversion.isLoading` porque `usePaymentConversion` não zera `data` ao trocar
      de filtro — as linhas do recorte anterior sobrevivem até a nova resposta chegar.
      Sem isso, o card seguiria anunciando o número do período antigo enquanto os
      `MetricCard` do bloco ao lado já estão acinzentados. Mesma razão pela qual o
      próprio `useDiscoveryCard` trata toggle carregando como indisponível: não piscar
      um convite que pode não valer.
    */
    hasSales: !conversion.isLoading && pixAbandonado.cobrancas > 0,
  });

  /*
    Um convite por tela. Os predicados de descoberta não caem em comunidades diferentes: 68% das
    comunidades ativas falham os oito, e 91% falham sete ou oito. Sem o desempate, o criador típico
    abriria a home com três avisos ao mesmo tempo — e a resposta a uma parede de avisos é aprender a
    ignorar o X. A ordem e o porquê dela estão em `pickDiscoveryCard`.
  */
  const {
    show: showReferralCard,
    isPending: referralPending,
    dismiss: dismissReferral,
    referralRate,
  } = useDiscoveryCard({
    feature: 'referral',
    toggle: 'referVib',
    /*
      Sem `permission`: a indicação é da CONTA. Ela mora em `/referVib`, não aparece em `FeatureName`,
      e a própria página se protege só por toggle.
    */
    selectedIds: filters.botConfigIds,
    /*
      `true`: este é o único dos quatro convites cujo gatilho não é uma evidência do recorte. Não existe
      número próprio do criador antes da adoção — quem nunca indicou tem zero indicados —, então o que
      o card mostra é a fatia que ele recebe. Ver a nota em `pickDiscoveryCard` sobre a posição dele.
    */
    hasSales: true,
  });

  /*
    A pendência de um candidato inclui o dado de RELATÓRIO que alimenta o `hasSales` dele, e não só o
    que o hook sabe (toggle e status). Enquanto `useBotFunnel` e `usePaymentConversion` carregam, os
    dois gates acima produzem `hasSales: false` de propósito — e `false` é indistinguível de "não tem
    venda". Sem contar esse carregamento como pendência, o convite de cadência (o de menor prioridade
    dos três) apareceria primeiro só porque o relatório demorou, para ser trocado quando ele chegar.

    `isStale` além de `isLoading`, e não por excesso de zelo: os dois hooks só chamam
    `setIsLoading(true)` dentro do efeito, que roda depois do commit. Trocar só o período mantém os
    mesmos ids, então nenhum `useDiscoveryCard` fica pendente, e existe uma passada de render com
    `isLoading === false` e a evidência do período ANTERIOR. `isStale` compara os filtros a que o dado
    responde com os de agora, e por isso já vale nessa passada.

    O que torna isso pior que um flicker é a dispensa: o card de cadência fica calado 7 dias, e os
    outros 30. Um X clicado no convite errado cala um convite que o criador nunca escolheu ver.
  */
  const cardVisivel = pickDiscoveryCard({
    remarketing:
      remarketingPending || funnel.isLoading || funnel.isStale
        ? 'pending'
        : showRemarketingCard,
    downsell:
      downsellPending || conversion.isLoading || conversion.isStale
        ? 'pending'
        : showDownsellCard,
    scheduleMessages: schedulePending ? 'pending' : showScheduleCard,
    referral: referralPending ? 'pending' : showReferralCard,
  });

  return (
    <>
      <TopCards
        filters={filters}
        sales={sales}
        conversion={conversion}
        funnel={funnel}
        recurring={recurring}
        priorSalesCount={priorSalesCount}
      />

      <FunnelSection funnel={funnel} />

      {cardVisivel === 'remarketing' && (
        <RemarketingDiscoveryBlock
          semCobranca={semCobranca}
          variasComunidades={filters.botConfigIds.length > 1}
          productCode={remarketingProductCode}
          onDismiss={dismissRemarketing}
        />
      )}
      <TopOffers funnel={funnel} isReady={isFunnelReady} />

      <PaymentBlock
        conversion={conversion}
        href={reportHref('/reports/conversion', filters)}
      />

      {cardVisivel === 'downsell' && (
        <DownsellDiscoveryBlock
          pixAbandonado={pixAbandonado}
          productCode={downsellProductCode}
          onDismiss={dismissDownsell}
        />
      )}

      <RecurringBlock
        recurring={recurring}
        href={reportHref('/reports/recorrencia', filters)}
      />

      {cardVisivel === 'scheduleMessages' && (
        <ScheduleMessagesDiscoveryBlock
          evidencia={scheduleEvidence}
          onDismiss={dismissSchedule}
        />
      )}

      {cardVisivel === 'referral' && (
        <ReferralDiscoveryBlock
          rate={referralRate}
          onDismiss={dismissReferral}
        />
      )}
      <TimeToConvertBlock
        timing={timing}
        href={reportHref('/reports/tempo-ate-converter', filters)}
      />
      <CustomerValueBlock
        customer={customer}
        href={reportHref('/reports/valor-do-cliente', filters)}
      />
      <RemarketingBlock
        remarketing={remarketing}
        href={reportHref('/reports/remarketing', filters)}
      />

      {historySlot}

      <div className="mt-2xl">
        <VibAccordion title="Ver detalhamento" withBorder>
          <DetailPanels
            funnel={funnel}
            recurring={recurring}
            isFunnelReady={isFunnelReady}
          />
        </VibAccordion>
      </div>

      {groupSlot}
    </>
  );
}

/* Topo: quatro números                                                       */

/** Valor em destaque e contagem na legenda: cinco reembolsos de R$ 20 e de R$ 600 são prejuízos incomparáveis. */
function RefundCard({
  sales,
  priorSalesCount,
}: {
  sales: ReturnType<typeof useSalesSummaryPair>;
  priorSalesCount: number;
}) {
  const { data, previous, hasCurrent, isLoading } = sales;
  const count = Number(data.refundTotalCount || 0);
  const value = Number(data.refundTotalValue || 0);

  if (!isLoading && !hasCurrent)
    return (
      <UnavailableCard
        title="Reembolsos"
        help="A consulta do período selecionado não voltou. O card fica em branco de propósito: mostrar R$ 0,00 aqui afirmaria que ninguém pediu reembolso, e o que houve foi uma requisição que falhou."
      />
    );

  return (
    <MetricCard
      title="Reembolsos"
      isLoading={isLoading}
      value={formatMoney(value)}
      caption={count === 1 ? '1 reembolso' : `${count} reembolsos`}
      help="Valor líquido devolvido em reembolsos, e quantos foram. Conta pela data da VENDA, não do reembolso: um reembolso de hoje sobre uma venda do mês passado conta no mês da venda, e é por isso que este card nunca fecha com o extrato do período. Por essa mesma razão os meses recentes tendem a subir depois — a venda de ontem ainda pode ser reembolsada amanhã."
      indicator={
        <Delta
          current={value}
          previous={Number(previous?.refundTotalValue ?? 0)}
          sample={priorSalesCount}
          isAvailable={Boolean(previous)}
          invert
        />
      }
    />
  );
}

/**
 * Responde "este número obedece ao filtro?" sem depender da ajuda de cada card. A divisão é condicional
 * porque o primeiro grupo é: sem base de assinantes, a linha separaria o grupo do cabeçalho.
 */
const GroupLabel = ({
  title,
  subtitle,
  withDivider = false,
}: {
  title: string;
  subtitle: string;
  withDivider?: boolean;
}) => (
  <div
    className={`pb-main ${
      withDivider ? 'border-t border-[#E5E7EB] pt-2xl' : ''
    }`}
  >
    <Typography variant="h2">{title}</Typography>
    <Typography className="text-sm text-lightBlack">{subtitle}</Typography>
  </div>
);

/** Travessão no lugar do número: falha de rede não pode virar afirmação sobre o negócio. */
function UnavailableCard({ title, help }: { title: string; help: string }) {
  return (
    <MetricCard
      title={title}
      isLoading={false}
      value="—"
      caption="não foi possível carregar"
      help={help}
      indicator={null}
    />
  );
}

/** Os dois cards de venda juntos, para o ramo que mostra os dois. */
function SalesCards({
  sales,
  priorSalesCount,
}: {
  sales: ReturnType<typeof useSalesSummaryPair>;
  priorSalesCount: number;
}) {
  return (
    <>
      <RevenueCard sales={sales} priorSalesCount={priorSalesCount} />
      <SalesCountCard sales={sales} priorSalesCount={priorSalesCount} />
    </>
  );
}

/** Receita líquida do período. Sai sozinho no ramo de assinatura, onde divide o topo com o MRR. */
function RevenueCard({
  sales,
  priorSalesCount,
}: {
  sales: ReturnType<typeof useSalesSummaryPair>;
  priorSalesCount: number;
}) {
  const { data, previous, hasCurrent, isLoading } = sales;
  const salesValueSeries = data.sales.map((row) => ({
    value: row.paymentValue,
    date: row.date,
  }));

  if (!isLoading && !hasCurrent)
    return (
      <UnavailableCard
        title="Receita (valor líquido)"
        help="A consulta do período selecionado não voltou. O card fica em branco de propósito: mostrar R$ 0,00 aqui afirmaria que não houve venda, e o que houve foi uma requisição que falhou."
      />
    );

  return (
    <SalesChart
      title="Receita (valor líquido)"
      isLoading={isLoading}
      isMoneyChart
      label={data.salesTotalValue || ''}
      chartData={salesValueSeries}
      customHelpTooltip={
        <CustomHelpTooltip
          description={
            <Typography className="text-white text-xs p-sm">
              Valor líquido do produtor, já descontadas taxas e comissões. Conta
              pela data do PAGAMENTO. Não bate com &quot;valor gerado&quot; no
              detalhamento, que é bruto e conta pela data em que a cobrança foi
              criada.
            </Typography>
          }
        />
      }
      profitIndicator={
        <Delta
          current={Number(data.salesTotalValue || 0)}
          previous={Number(previous?.salesTotalValue ?? 0)}
          sample={priorSalesCount}
          isAvailable={Boolean(previous)}
        />
      }
    />
  );
}

function SalesCountCard({
  sales,
  priorSalesCount,
}: {
  sales: ReturnType<typeof useSalesSummaryPair>;
  priorSalesCount: number;
}) {
  const { data, previous, hasCurrent, isLoading } = sales;
  const salesCountSeries = data.sales.map((row) => ({
    value: row.salesCount,
    date: row.date,
  }));

  if (!isLoading && !hasCurrent)
    return (
      <UnavailableCard
        title="Vendas pagas (quantidade)"
        help="A consulta do período selecionado não voltou. O card fica em branco de propósito: mostrar zero aqui afirmaria que não houve venda, e o que houve foi uma requisição que falhou."
      />
    );

  return (
    <SalesChart
      title="Vendas pagas (quantidade)"
      isLoading={isLoading}
      label={data.salesTotalCount || ''}
      chartData={salesCountSeries}
      customHelpTooltip={
        <CustomHelpTooltip
          description={
            <Typography className="text-white text-xs p-sm">
              Vendas efetivamente pagas no período, pela data do pagamento.
            </Typography>
          }
        />
      }
      profitIndicator={
        <Delta
          current={Number(data.salesTotalCount || 0)}
          previous={priorSalesCount}
          sample={priorSalesCount}
          isAvailable={Boolean(previous)}
        />
      }
    />
  );
}

/**
 * Os quatro números do instante de hoje, nenhum deles obedecendo ao filtro. Títulos iguais aos do relatório
 * de recorrência para o card e a linha que o detalha serem reconhecíveis como a mesma medida.
 */
function BaseNowCards({
  recurring,
}: {
  recurring: ReturnType<typeof useRecurringRevenue>;
}) {
  const { snapshot } = recurring;
  const atRisk = describeAtRiskMrr(snapshot, recurring.coverage, formatMoney);
  const population = resolvePopulation(snapshot, recurring.coverage);

  // O hook zera o snapshot em falha, e zero em negrito lê como base sem valor em vez de número ausente.
  const money = (value: number) =>
    recurring.hasFailed
      ? '—'
      : formatCoveredMoney(
          value,
          recurring.coverage,
          'current',
          formatMoney,
          snapshot,
        );

  const caption = (text: string) =>
    recurring.hasFailed ? 'não foi possível carregar' : text;

  return (
    <section className={GROUP_GAP}>
      <GroupLabel
        title="A base hoje"
        subtitle="Retrato da base de assinantes neste instante — não muda com o filtro de período"
      />

      {/* Cobertura de agora; a nota do accordion é `instant="opening"` e fala da série histórica. */}
      {recurring.hasLoaded && !recurring.hasFailed && (
        <MrrCoverageNote
          coverage={recurring.coverage}
          instant="current"
          snapshot={snapshot}
        />
      )}

      <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-4">
        <MetricCard
          title="MRR projetado"
          isLoading={recurring.isLoading}
          value={money(snapshot.mrr)}
          caption={caption(`hoje · ${describePopulation(population)}`)}
          indicator={null}
          help="A receita mensal que a sua base de assinantes ativos deve renovar, contada HOJE — não no período selecionado. Cada assinatura entra pelo valor da última cobrança dela, convertido para 30 dias. É projeção: conta só o que você ainda tem direito a receber. Só oferta de assinatura conta; pack, chamada de vídeo e oferta personalizada são venda avulsa e ficam de fora."
        />
        <MetricCard
          title="ARR projetado"
          isLoading={recurring.isLoading}
          value={money(snapshot.arr)}
          caption={caption('hoje · MRR projetado × 12')}
          indicator={null}
          help="O MRR projetado de hoje em doze meses. Não é previsão: assume que a base de hoje renova inalterada, sem entrada nem saída. Serve para ordem de grandeza, não para planejamento."
        />
        <MetricCard
          title="Ticket médio mensal"
          isLoading={recurring.isLoading}
          value={money(snapshot.averageMonthlyTicket)}
          caption={caption('hoje · MRR projetado ÷ assinantes ativos')}
          indicator={null}
          help="Quanto cada assinante ativo vale por mês, em média. Como tudo é normalizado para 30 dias, planos de duração diferente ficam comparáveis. Não é o mesmo que o ticket médio da venda: este é por ASSINANTE por mês, aquele é por VENDA."
        />
        <MetricCard
          title="MRR em risco"
          isLoading={recurring.isLoading}
          value={recurring.hasFailed ? '—' : atRisk.value}
          caption={caption(`hoje · ${atRisk.caption}`)}
          indicator={null}
          help="Parte do MRR que pertence a assinantes que já desligaram a renovação automática. Eles continuam ativos até o acesso vencer, então esse valor ainda está no MRR — mas vai sair, salvo se voltarem atrás. É a única perda que dá para ver ANTES de acontecer, e por isso a única em que dar um alô ainda muda o resultado. A cobertura declarada acima é da base INTEIRA e não garante esta fatia: se justamente as assinaturas sem período conhecido forem as que desligaram a renovação, este valor sai menor sem que a cobertura geral acuse."
        />
      </div>
    </section>
  );
}

/** Um grupo só, com as datas resolvidas embaixo do rótulo: é o que o filtro do topo controla. */
function PeriodCards({
  filters,
  sales,
  conversion,
  funnel,
  priorSalesCount,
  /** Se o grupo "A base hoje" está acima. Decide a linha divisória, não o conteúdo. */
  hasBaseGroup,
  /** Leads por comprador ocupa o quarto lugar quando não há MRR para mostrar acima. */
  showsFunnelRatio,
}: {
  filters: DashboardFilters;
  sales: ReturnType<typeof useSalesSummaryPair>;
  conversion: ReturnType<typeof usePaymentConversion>;
  funnel: ReturnType<typeof useBotFunnel>;
  priorSalesCount: number;
  hasBaseGroup: boolean;
  showsFunnelRatio: boolean;
}) {
  const { totals, priorTotals } = conversion;

  // Contagem na frente: "8 cobranças não pagas" é tarefa; num volume de vinte, o percentual só oscila.
  const unpaidCount = totals.createdCount - totals.paidCount;
  const unpaidValue = totals.createdGrossValue - totals.paidGrossValue;
  const priorUnpaidCount = priorTotals
    ? priorTotals.createdCount - priorTotals.paidCount
    : undefined;

  const hasFunnelRate = !funnel.isLoading && funnel.hasData;

  return (
    <section className={GROUP_GAP}>
      <GroupLabel
        title="No período"
        subtitle={`de ${parseDate(filters.startDate, {
          type: 'date',
        })} a ${parseDate(filters.endDate, { type: 'date' })}`}
        withDivider={hasBaseGroup}
      />

      <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-4">
        <RevenueCard sales={sales} priorSalesCount={priorSalesCount} />
        <SalesCountCard sales={sales} priorSalesCount={priorSalesCount} />
        <RefundCard sales={sales} priorSalesCount={priorSalesCount} />

        {/* Em falha o hook cai em emptyTotals, e "0 cobranças não pagas" leria como boa notícia. */}
        {conversion.hasFailed ? (
          <UnavailableCard
            title="Cobranças não pagas"
            help="A consulta do período selecionado não voltou. O card fica em branco de propósito: mostrar 0 aqui afirmaria que todas as cobranças foram pagas, e o que houve foi uma requisição que falhou."
          />
        ) : (
          <MetricCard
            title="Cobranças não pagas"
            isLoading={conversion.isLoading}
            value={String(unpaidCount)}
            caption={`${formatMoney(unpaidValue)} na mesa · ${formatRate(
              totals.paidRate,
            )} das geradas foram pagas`}
            help="Cobranças geradas no período que ainda não foram pagas. Dinheiro já conquistado que não entrou — é a lista de quem vale um lembrete. A cobrança conta pela data em que foi GERADA, então uma de ontem paga hoje continua contando ontem; por isso este bloco nunca fecha com o card de receita."
            indicator={
              <Delta
                current={unpaidCount}
                previous={priorUnpaidCount ?? 0}
                sample={priorTotals?.createdCount ?? 0}
                isAvailable={priorUnpaidCount !== undefined}
                invert
              />
            }
          />
        )}

        {/* leadsPerPurchaser e não startsPerSale: sem /start nenhum, o outro caía em "sem medição". */}
        {showsFunnelRatio && (
          <MetricCard
            title="Leads por comprador"
            isLoading={funnel.isLoading}
            value={
              hasFunnelRate && funnel.data.leadsPerPurchaser
                ? `1 : ${formatSalesRatio(funnel.data.leadsPerPurchaser)}`
                : '—'
            }
            caption={
              hasFunnelRate && funnel.data.leadsPerPurchaser
                ? `1 comprador a cada ${formatSalesRatio(
                    funnel.data.leadsPerPurchaser,
                  )} leads que falam com o bot`
                : 'sem medição suficiente no período'
            }
            help="Quantos leads precisam falar com o bot para um deles comprar. Conta pessoas, não vendas: quem compra duas vezes conta uma vez aqui. Conta toda interação de quem é novo ou volta depois de 6h, não só quem envia /start. Só conta o bot: venda feita direto no checkout da web só entra se o comprador demonstrou intenção no bot antes."
            indicator={null}
          />
        )}
      </div>
    </section>
  );
}

/** O grupo da base só existe onde há base: quatro cards zerados leem como fracasso, não como "não se aplica". */
function TopCards({
  filters,
  sales,
  conversion,
  funnel,
  recurring,
  priorSalesCount,
}: {
  filters: DashboardFilters;
  sales: ReturnType<typeof useSalesSummaryPair>;
  conversion: ReturnType<typeof usePaymentConversion>;
  funnel: ReturnType<typeof useBotFunnel>;
  recurring: ReturnType<typeof useRecurringRevenue>;
  priorSalesCount: number;
}) {
  /**
   * Falha e payload parcial não contam como "não tem base": trocar o layout afirma "você não vende
   * assinatura", e a requisição recusada cai no mesmo zero de quem realmente não vende.
   */
  const showsSubscriptionBase = hasSubscriptionBase({
    hasLoaded: recurring.hasLoaded,
    isLoading: recurring.isLoading,
    hasFailed: recurring.hasFailed,
    snapshot: recurring.snapshot,
    coverage: recurring.coverage,
  });

  return (
    <>
      {showsSubscriptionBase && <BaseNowCards recurring={recurring} />}

      <PeriodCards
        filters={filters}
        sales={sales}
        conversion={conversion}
        funnel={funnel}
        priorSalesCount={priorSalesCount}
        hasBaseGroup={showsSubscriptionBase}
        showsFunnelRatio={!showsSubscriptionBase}
      />
    </>
  );
}

/* Onde vaza                                                                  */

function FunnelSection({
  funnel,
}: {
  funnel: ReturnType<typeof useBotFunnel>;
}) {
  const { data, coverage, hasData, isLoading } = funnel;

  const leadsByStage = useMemo(
    () =>
      data.stages.reduce<Record<string, number>>((acc, row) => {
        acc[row.stage] = row.leads;
        return acc;
      }, {}),
    [data.stages],
  );

  /**
   * Só as etapas contadas em pessoas: `paidSales` conta vendas, e na mesma escala "Pagou" passaria a etapa
   * anterior sem ninguém ter voltado. Não há recorte de compradores por etapa.
   */
  const macroRows = useMemo(
    () =>
      MACRO_STAGES.map(({ label, stage }) => ({
        label,
        value: leadsByStage[stage] ?? 0,
      })),
    [leadsByStage],
  );

  const biggestDrop = useMemo(
    () => resolveBiggestDrop(leadsByStage),
    [leadsByStage],
  );

  const base = macroRows[0]?.value ?? 0;

  return (
    <section className={GROUP_GAP}>
      <div className="flex items-center gap-sm pb-main">
        <div>
          <Typography variant="h2">Onde as pessoas param</Typography>
          <Typography className="text-sm text-lightBlack">
            Da primeira conversa até a cobrança paga
          </Typography>
        </div>
        <FunnelHelpTooltip />
      </div>

      {/* `coverage` cruza o collectionStartedAt retido com as datas novas: em refetch descreve seleção inexistente. */}
      {!isLoading && hasData && coverage.kind === 'partial' && (
        <Alert variant="warning">
          <Typography className="text-sm">
            <strong>Período parcialmente medido.</strong> A medição começou em{' '}
            {formatDay(coverage.startedAt)}, então{' '}
            {describeUncovered(coverage.uncoveredDays)} do período selecionado
            não têm dados.
          </Typography>
          <Typography className="text-sm pt-sm">
            Os números abaixo cobrem só a parte medida, e por isso ficam menores
            que os cards de venda, que cobrem o período inteiro. As taxas
            continuam válidas — comparam etapas dentro da mesma janela.
          </Typography>
        </Alert>
      )}

      {isLoading && (
        <Paper className="p-main">
          <Typography className="text-sm text-lightBlack">
            Carregando…
          </Typography>
        </Paper>
      )}

      {!isLoading && !hasData && (
        <EmptyFunnel collectionStartedAt={data.collectionStartedAt} />
      )}

      {!isLoading && hasData && (
        <>
          {biggestDrop && (
            <Alert variant="info" className="mb-main">
              <Typography className="text-sm">
                Maior perda: <strong>{biggestDrop.lost} pessoas</strong> entre
                &quot;{biggestDrop.from}&quot; e &quot;{biggestDrop.to}&quot;.
              </Typography>
            </Alert>
          )}

          <Paper className="p-main">
            <div className="grid gap-main">
              {macroRows.map((row) => (
                <div key={row.label}>
                  <div className="flex items-baseline justify-between">
                    <Typography className="text-sm">{row.label}</Typography>
                    <Typography className="text-sm font-bold">
                      {row.value}
                    </Typography>
                  </div>
                  <VibLinearProgress
                    progress={base ? (row.value / base) * 100 : 0}
                  />
                </div>
              ))}
            </div>

            {/* Fora da escala das barras: está em vendas, não em pessoas. */}
            <div className="mt-main pt-main border-t border-[#f3f4f6] flex items-baseline justify-between">
              <div>
                <Typography className="text-sm">Vendas pagas</Typography>
                <Typography className="text-xs text-lightBlack">
                  transações, não pessoas — quem comprou duas vezes conta duas
                </Typography>
              </div>
              <Typography className="text-sm font-bold">
                {data.paidSales}
              </Typography>
            </div>
          </Paper>
        </>
      )}
    </section>
  );
}

/**
 * Queda em absoluto, não em percentual: 90% de 10 custa 9 vendas, 20% de 800 custa 160.
 *
 * Só entre as etapas sequenciais, e parando na cobrança gerada. O funil não é monotônico — quem chega por
 * oferta promovida pula o menu — e `paidSales` conta vendas, não pessoas; nos dois casos a diferença lida
 * como perda mandaria consertar problema que não existe.
 */
function resolveBiggestDrop(leadsByStage: Record<string, number>) {
  const points = SEQUENTIAL_STAGES.map((stage) => ({
    label: BOT_FUNNEL_STAGE_LABELS[stage],
    value: leadsByStage[stage] ?? 0,
  }));

  let biggest: { from: string; to: string; lost: number } | null = null;

  for (let index = 1; index < points.length; index += 1) {
    const lost = points[index - 1].value - points[index].value;

    if (lost > 0 && (!biggest || lost > biggest.lost))
      biggest = {
        from: points[index - 1].label,
        to: points[index].label,
        lost,
      };
  }

  return biggest;
}

/* Ofertas                                                                    */

/** Por vistas e não por vendas: ordenado por venda, a oferta que aparece muito e não converte sai da lista. */
function TopOffers({
  funnel,
  isReady,
}: {
  funnel: ReturnType<typeof useBotFunnel>;
  isReady: boolean;
}) {
  const rows = useMemo(
    () =>
      [...funnel.data.byOffer]
        .sort((a, b) => b.impressions - a.impressions)
        .slice(0, 5),
    [funnel.data.byOffer],
  );

  // isReady, não hasData: durante o refetch a resposta antiga continua em memória, e mostrar as ofertas
  // do período anterior sob os filtros novos é pior que não mostrar nada.
  if (!isReady || !rows.length) return null;

  return (
    <section className={GROUP_GAP}>
      <div className="flex items-center gap-sm pb-main">
        <div>
          <Typography variant="h2">Ofertas</Typography>
          <Typography className="text-sm text-lightBlack">
            As 5 mais exibidas no bot
          </Typography>
        </div>
        <CustomHelpTooltip
          description={
            <div className="grid gap-sm p-sm">
              <Typography className="text-white text-xs">
                <strong>Viram</strong> é o denominador que faltava: sem ele, uma
                oferta que ninguém compra parece igual a uma que ninguém vê.
              </Typography>
              <Typography className="text-white text-xs">
                <strong>Escolheram baixo</strong> = a oferta não atrai.{' '}
                <strong>Escolheram alto e vendas baixas</strong> = ela atrai e
                perde a pessoa depois, no cadastro ou no pagamento.
              </Typography>
              <Typography className="text-white text-xs">
                <strong>
                  Vendas são transações, &quot;viram&quot; são pessoas.
                </strong>{' '}
                Quem comprou a mesma oferta duas vezes conta como uma pessoa e
                duas vendas, então &quot;Vendas / viram&quot; pode passar de
                100% — não é erro.
              </Typography>
            </div>
          }
        />
      </div>

      {/* ReportTable porque a lista completa, no detalhamento, é a mesma tabela com mais linhas. */}
      <ReportTable
        headers={['Oferta', 'Viram', 'Escolheram', 'Vendas', 'Vendas / viram']}
        isEmpty={false}
      >
        {rows.map((row) => (
          <Row key={row.offerId ?? 'no-offer'}>
            <Cell>
              {row.offerName ??
                (row.offerId === null
                  ? '(sem oferta)'
                  : `Oferta #${row.offerId} (removida)`)}
            </Cell>
            <Cell>{row.impressions}</Cell>
            <Cell>{row.selected}</Cell>
            <Cell>{row.paidSales}</Cell>
            {/* Duas casas, como a tabela completa: senão a mesma oferta sai "0.6%" aqui e "0.62%" ali. */}
            <Cell>{formatFunnelRate(row.impressionToSaleRate)}</Cell>
          </Row>
        ))}
      </ReportTable>
    </section>
  );
}

/* Blocos por domínio                                                         */

/** O link é a fronteira entre as camadas: o dashboard responde a pergunta, o relatório mostra linha a linha. */
/** Fica na tela: dois blocos se escondem por falta de dado, e sumir na falha leria como "não tenho base". */
function FailedBlock({
  title,
  subtitle,
  href,
}: {
  title: string;
  subtitle: string;
  href?: string;
}) {
  return (
    <section className={GROUP_GAP}>
      <BlockHeader title={title} subtitle={subtitle} href={href} />
      <ReportUnavailable />
    </section>
  );
}

function BlockHeader({
  title,
  subtitle,
  href,
  help,
}: {
  title: string;
  subtitle: string;
  href?: string;
  help?: React.ReactNode;
}) {
  return (
    <div className="pb-main flex items-center justify-between gap-main">
      <div className="flex items-center gap-sm">
        <div>
          <Typography variant="h2">{title}</Typography>
          <Typography className="text-sm text-lightBlack">
            {subtitle}
          </Typography>
        </div>
        {help}
      </div>
      {href && (
        <Link
          href={href}
          className="text-sm text-primary whitespace-nowrap flex items-center gap-[4px]"
        >
          Ver detalhes
          <ArrowForwardIcon fontSize="small" />
        </Link>
      )}
    </div>
  );
}

/** Grade padrão dos blocos: três cards, que é o que cabe sem virar parede de número. */
const BlockGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid gap-main md:grid-cols-2 2xl:grid-cols-3">{children}</div>
);

/** Fora do accordion porque os três números são de operação, não de análise. */
function PaymentBlock({
  conversion,
  href,
}: {
  conversion: ReturnType<typeof usePaymentConversion>;
  href?: string;
}) {
  const { totals, isLoading, hasFailed } = conversion;

  if (hasFailed)
    return (
      <FailedBlock
        title="Pagamento das cobranças"
        subtitle="O que acontece depois que a cobrança é gerada"
        href={href}
      />
    );

  return (
    <section className={GROUP_GAP}>
      <BlockHeader
        title="Pagamento das cobranças"
        subtitle="O que acontece depois que a cobrança é gerada"
        href={href}
      />
      <BlockGrid>
        <MetricCard
          title="Pagas em até 24h"
          isLoading={isLoading}
          value={formatRate(totals.paidWithin24hRate)}
          caption={`${totals.paidWithin24hCount} de ${totals.createdCount} geradas`}
          help="Percentual das cobranças geradas que foi pago em até 24 horas. É a janela em que um PIX realmente vive: passado esse tempo, a chance de pagamento cai muito."
          indicator={null}
        />
        <MetricCard
          title="Tempo médio até pagar"
          isLoading={isLoading}
          value={formatMinutes(totals.averageMinutesToPay)}
          caption="considera só as cobranças pagas"
          help="Média entre gerar a cobrança e receber o pagamento. A média é sensível a cobrança de cauda longa — uma paga uma semana depois puxa o número para cima sozinha — então leia junto com 'pagas em até 24h', que é mais estável."
          indicator={null}
        />
        <MetricCard
          title="Valor pago / gerado"
          isLoading={isLoading}
          value={formatMoney(totals.paidGrossValue)}
          caption={`de ${formatMoney(totals.createdGrossValue)} gerados`}
          help="Valor BRUTO cobrado, antes de taxas e comissões — diferente do card de receita no topo, que já desconta os custos. Os dois números não batem por isso."
          indicator={null}
        />
      </BlockGrid>
    </section>
  );
}

/**
 * O convite para ligar a recuperação de Pix, colado no bloco de pagamento porque é lá
 * que o número dele nasce: cobrança gerada e não paga.
 *
 * Mora na home e não no relatório de Carrinhos Abandonados — que era o lugar óbvio pelo
 * contexto — porque a home é a tela onde todo criador cai, e o relatório quase não é
 * aberto. Quem chega pelo relatório continua atendido pelo passo do checklist.
 */
function DownsellDiscoveryBlock({
  pixAbandonado,
  productCode,
  onDismiss,
}: {
  pixAbandonado: { cobrancas: number; valor: number };
  productCode?: string;
  onDismiss: () => void;
}) {
  const umaSo = pixAbandonado.cobrancas === 1;

  return (
    <DiscoveryCard
      title={`${pixAbandonado.cobrancas} ${
        umaSo ? 'cobrança Pix' : 'cobranças Pix'
      } ${umaSo ? 'ficou' : 'ficaram'} sem pagamento no período selecionado`}
      body={`São ${parseMoneyValue(
        pixAbandonado.valor,
      )} que não entraram. Hoje essas pessoas recebem o mesmo preço de novo; com a recuperação ligada, recebem um preço menor — e você escolhe quanto.`}
      ctaLabel="Ativar recuperação com 20%"
      ctaHref={`/products/${productCode}/downsells/create?sugestao=recuperacao`}
      onDismiss={onDismiss}
    />
  );
}

/**
 * O convite para ligar a recuperação, colado ao funil porque é lá que o número dele nasce: quem
 * conversou e não comprou.
 *
 * A cópia NÃO afirma que a comunidade está sem mensagens escritas. O status da API devolve um booleano
 * só — "tem recuperação ativa" exige envio ligado E mensagem não apagada —, então há comunidades com a
 * sequência pronta e o envio desligado, para quem essa frase seria falsa. "Hoje elas não recebem nada"
 * é verdade nas duas situações.
 */
function RemarketingDiscoveryBlock({
  semCobranca,
  variasComunidades,
  productCode,
  onDismiss,
}: {
  semCobranca: number;
  /**
   * O funil não devolve dimensão por comunidade — `botConfigIds` só existe no filtro —, então o número é
   * do recorte inteiro. Com várias comunidades selecionadas, e o padrão da tela é marcar todas as ativas,
   * dizer "seu bot" no singular seria falso.
   */
  variasComunidades: boolean;
  productCode?: string;
  onDismiss: () => void;
}) {
  const umaSo = semCobranca === 1;

  return (
    <DiscoveryCard
      title={`${semCobranca} ${
        umaSo ? 'pessoa conversou' : 'pessoas conversaram'
      } com ${variasComunidades ? 'seus bots' : 'seu bot'} e não ${
        umaSo ? 'chegou' : 'chegaram'
      } a gerar uma cobrança`}
      body="A recuperação automática fala com elas depois de um tempo de silêncio, e para assim que a pessoa compra. Hoje elas não recebem nada."
      ctaLabel="Ativar a recuperação"
      ctaHref={`/products/${productCode}/remarketing`}
      onDismiss={onDismiss}
    />
  );
}

/**
 * O convite para voltar a falar com a base, colado ao bloco de movimento dela.
 *
 * O número é a audiência que está parada: contatos do bot das comunidades atrasadas —
 * inclusive quem nunca assinou, que é justamente o valor de uma mensagem agendada.
 *
 * A rota leva as comunidades atrasadas, e não o recorte inteiro: a tela de envio tem
 * seleção própria (`selectedLocalStorageId="scheduleMessage"`) e não herda a do
 * dashboard, então sem carregar os ids o card anunciaria um conjunto e o envio abriria
 * apontado para outro.
 */
function ScheduleMessagesDiscoveryBlock({
  evidencia,
  onDismiss,
}: {
  evidencia: {
    contacts: number;
    daysSinceLastMessage: number | null;
    ids: number[];
  };
  onDismiss: () => void;
}) {
  /*
    Sem `productCode` de propósito: a tela de envio é GLOBAL (`/scheduleMessages`), não
    por produto. O que ela precisa saber é quais comunidades, e isso vai na rota.
  */
  const onde = evidencia.ids.length > 1 ? 'nos seus bots' : 'no seu bot';
  const silencio =
    evidencia.daysSinceLastMessage === null
      ? 'e nunca enviou uma mensagem para eles'
      : `e não envia uma mensagem há ${evidencia.daysSinceLastMessage} dias`;

  return (
    <DiscoveryCard
      title={`Você tem ${evidencia.contacts} ${
        evidencia.contacts === 1 ? 'contato' : 'contatos'
      } ${onde} ${silencio}`}
      body="Uma mensagem agendada alcança todos eles de uma vez — inclusive quem nunca assinou."
      ctaLabel="Programar uma mensagem"
      ctaHref={`/scheduleMessages/toContacts?sugestao=cadencia&comunidades=${evidencia.ids.join(
        ',',
      )}`}
      onDismiss={onDismiss}
    />
  );
}

/**
 * O convite do "indique e ganhe".
 *
 * É o único dos convites de descoberta sem número do próprio criador: quem nunca indicou tem zero
 * indicados, e não existe nada no recorte dele que meça essa oportunidade. O que resta é a regra — a
 * fatia que ele recebe —, e por isso o card é o mais fraco dos quatro. Quem faz o trabalho aqui é o
 * passo do hub, que aparece sem disputar vez com ninguém.
 *
 * `2%` para 63.926 dos 63.935 criadores, mas lido de `referralRate`: é o número dele, e nove criadores
 * têm outro.
 */
function ReferralDiscoveryBlock({
  rate,
  onDismiss,
}: {
  rate: number;
  onDismiss: () => void;
}) {
  /*
    `Intl`, e não o `parsePercent` do projeto: aquele devolve `2.00%` — com ponto, num texto em
    português, e com duas casas que não dizem nada num número redondo. Aqui sai `2%`, e `2,5%` para
    quem tem uma fatia quebrada.
  */
  const fatia = new Intl.NumberFormat('pt-BR', {
    style: 'percent',
    maximumFractionDigits: 2,
  }).format(rate);

  return (
    <DiscoveryCard
      title={`Cada criador que você indicar te paga ${fatia} de tudo que vender`}
      body="Não é por uma venda: é uma parte de tudo que ele faturar na VibX, enquanto ele estiver aqui. Seu link de indicação já existe."
      ctaLabel="Pegar meu link"
      ctaHref="/referVib"
      onDismiss={onDismiss}
    />
  );
}

/** O que entrou e o que saiu da base no período. O MRR de hoje já está no topo. */
function RecurringBlock({
  recurring,
  href,
}: {
  recurring: ReturnType<typeof useRecurringRevenue>;
  href?: string;
}) {
  const { totals, snapshot, isLoading, hasFailed } = recurring;
  const population = resolvePopulation(snapshot, recurring.coverage);

  if (hasFailed)
    return (
      <FailedBlock
        title="Movimento da base"
        subtitle="Quem entrou e quem saiu no período selecionado"
        href={href}
      />
    );

  // Escondido só quando o relatório disse que não há o que mostrar; ausência não é zero.
  if (
    !showsBaseMovement({
      hasLoaded: recurring.hasLoaded,
      isLoading,
      hasFailed,
      population,
      churnedSubscribers: totals.churnedSubscribers,
    })
  )
    return null;

  return (
    <section className={GROUP_GAP}>
      <BlockHeader
        title="Movimento da base"
        subtitle="Quem entrou e quem saiu no período selecionado"
        href={href}
      />
      <BlockGrid>
        <MetricCard
          title="Churn no período"
          isLoading={isLoading}
          value={formatRate(totals.churnRate)}
          caption={`${totals.churnedSubscribers} saíram · ${totals.newSubscribers} entraram`}
          help="De todos os assinantes que a comunidade teve no período — os que já estavam no começo mais os que entraram — a fatia que saiu. O denominador inclui quem entrou de propósito: sem isso, alguém que entra e sai dentro do mesmo período apareceria na conta de cima sem aparecer na de baixo, e uma comunidade pequena poderia mostrar churn acima de 100%."
          indicator={null}
        />
        <MetricCard
          title="Saldo de MRR"
          isLoading={isLoading}
          value={formatSignedMoney(totals.netMrr)}
          caption={`${formatMoney(totals.newMrr)} entrou · ${formatMoney(
            totals.churnedMrr,
          )} saiu`}
          help="Quanto a receita mensal recorrente cresceu ou encolheu no período: o que entrou de assinante novo menos o que saiu com quem cancelou. Negativo significa que a base está encolhendo em dinheiro, mesmo que o número de assinantes tenha subido."
          indicator={null}
        />
        {/* Dois cards: "Ticket médio mensal" é valor do instante e subiu para o grupo da base. */}
      </BlockGrid>
    </section>
  );
}

/** `averageDaysObserved` na legenda, não na ajuda: numa coorte curta o LTV parece teto sendo começo. */
function CustomerValueBlock({
  customer,
  href,
}: {
  customer: ReturnType<typeof useCustomerValue>;
  href?: string;
}) {
  const { totals, isLoading, hasFailed } = customer;

  if (hasFailed)
    return (
      <FailedBlock
        title="Valor do cliente"
        subtitle="Quanto rende quem já comprou uma vez"
        href={href}
      />
    );

  return (
    <section className={GROUP_GAP}>
      <BlockHeader
        title="Valor do cliente"
        subtitle="Quanto rende quem já comprou uma vez"
        href={href}
      />
      <BlockGrid>
        <MetricCard
          title="Voltam a comprar"
          isLoading={isLoading}
          value={formatRate(totals.repeatRate)}
          caption={`${totals.repeatBuyers} de ${totals.buyers} compradores`}
          help="Fatia dos compradores que fez ao menos uma segunda compra, contando desde a primeira delas e não só dentro do período. É a métrica que separa audiência de base: cada ponto aqui vale mais que um ponto de conversão, porque não custa tráfego novo."
          indicator={null}
        />
        <MetricCard
          title="Receita por comprador"
          isLoading={isLoading}
          value={orDash(totals.buyers, formatMoney(totals.ltv))}
          caption={orDash(
            totals.buyers,
            `acompanhados por ${formatDays(totals.averageDaysObserved)}`,
          )}
          help="Tudo que um comprador rendeu desde a primeira compra, na média. Leia junto com o tempo de acompanhamento na legenda: coorte recente tem esse número perto do primeiro ticket por construção, porque ainda não teve tempo de recomprar. Não é teto, é o que já aconteceu."
          indicator={null}
        />
        <MetricCard
          title="Tempo até recomprar"
          isLoading={isLoading}
          value={orDash(
            totals.repeatBuyers,
            formatDays(totals.averageDaysToRepeat),
          )}
          caption={
            totals.crossSellBuyers
              ? `${totals.crossSellBuyers} compraram outra oferta`
              : 'entre a primeira e a segunda compra'
          }
          help="Dias entre a primeira e a segunda compra, na média, só de quem recomprou. Diz quando faz sentido falar de novo com quem comprou: bem antes dessa marca é cedo, bem depois é tarde."
          indicator={null}
        />
      </BlockGrid>
    </section>
  );
}

/** Quanto tempo o comprador leva entre chegar no bot e pagar. */
function TimeToConvertBlock({
  timing,
  href,
}: {
  timing: ReturnType<typeof useTimeToConvert>;
  href?: string;
}) {
  const { summary, isLoading, hasFailed } = timing;

  if (hasFailed)
    return (
      <FailedBlock
        title="Tempo até converter"
        subtitle="Quanto demora entre chegar no bot e pagar"
        href={href}
      />
    );

  return (
    <section className={GROUP_GAP}>
      <BlockHeader
        title="Tempo até converter"
        subtitle="Quanto demora entre chegar no bot e pagar"
        href={href}
      />
      <BlockGrid>
        <MetricCard
          title="Mediana até a compra"
          isLoading={isLoading}
          value={orDash(
            summary.converted,
            formatDuration(summary.medianSeconds),
          )}
          caption={`${summary.converted} de ${summary.arrivals} que chegaram`}
          help="Metade de quem comprou levou menos que isso. Mediana e não média de propósito: um comprador que voltou dois meses depois puxa a média e não muda a mediana, e é a mediana que diz quanto tempo a sua janela de venda realmente dura."
          indicator={null}
        />
        <MetricCard
          title="Compram no primeiro dia"
          isLoading={isLoading}
          value={formatRate(summary.withinDayRate)}
          caption={`${summary.convertedWithinDay} pessoas · ${formatRate(
            summary.withinHourRate,
          )} na primeira hora`}
          help="Fatia de quem chegou e comprou dentro de 24 horas. Quanto mais concentrado no primeiro dia, mais a venda depende do primeiro contato — e mais caro sai deixar o bot lento ou o preço escondido."
          indicator={null}
        />
        <MetricCard
          title="Conversão de quem chega"
          isLoading={isLoading}
          value={formatRate(summary.conversionRate)}
          caption="conta pela data em que a pessoa chegou"
          help="De todo mundo que chegou no bot no período, quem já comprou. Os períodos mais recentes aparecem mais baixos de propósito: as pessoas que chegaram ontem ainda têm tempo de comprar, e a linha vai subir sozinha."
          indicator={null}
        />
      </BlockGrid>
    </section>
  );
}

/** Sem disparo, os três zeros afirmariam fracasso em algo que o produtor nem tentou. */
function RemarketingBlock({
  remarketing,
  href,
}: {
  remarketing: ReturnType<typeof useRemarketingAttribution>;
  href?: string;
}) {
  const { summary, isLoading, hasFailed } = remarketing;

  if (hasFailed)
    return (
      <FailedBlock
        title="Remarketing"
        subtitle="O que as mensagens trouxeram de volta"
        href={href}
      />
    );

  if (isLoading || !summary.sends) return null;

  return (
    <section className={GROUP_GAP}>
      <BlockHeader
        title="Remarketing"
        subtitle="O que as mensagens trouxeram de volta"
        href={href}
      />
      <BlockGrid>
        <MetricCard
          title="Receita por disparo"
          isLoading={isLoading}
          value={formatMoney(summary.revenuePerSend)}
          caption={`${summary.sends} mensagens · ${formatMoney(
            summary.revenue,
          )} no total`}
          help="Quanto cada mensagem enviada trouxe de receita, na média. É o número que decide se vale mandar mais: comparado ao incômodo de mais um disparo, ele diz se a próxima mensagem paga o próprio custo."
          indicator={null}
        />
        <MetricCard
          title="Compraram depois"
          isLoading={isLoading}
          value={formatRate(summary.purchaseRate)}
          caption={`${summary.purchased} compras atribuídas`}
          help="Fatia dos disparos seguidos de compra dentro da janela de atribuição. A unidade é o ENVIO, não a pessoa: quem recebeu quatro mensagens aparece quatro vezes, e a compra é creditada à última antes dela."
          indicator={null}
        />
        <MetricCard
          title="Voltaram a interagir"
          isLoading={isLoading}
          value={formatRate(summary.returnRate)}
          caption={`${summary.returned} de ${summary.sends} disparos`}
          help="Fatia dos disparos que trouxe a pessoa de volta ao bot, comprando ou não. Retorno alto com compra baixa é problema de oferta, não de mensagem — a mensagem fez o trabalho dela."
          indicator={null}
        />
      </BlockGrid>
    </section>
  );
}

/* Detalhamento (accordion)                                                   */

/** Número único fica no dashboard; tabela que se fatia fica atrás do clique. */
function DetailPanels({
  funnel,
  recurring,
  isFunnelReady,
}: {
  funnel: ReturnType<typeof useBotFunnel>;
  recurring: ReturnType<typeof useRecurringRevenue>;
  isFunnelReady: boolean;
}) {
  return (
    <div className="grid gap-main">
      {/* Nove colunas respondem "o que aconteceu com a base", pergunta de quem já viu o MRR em risco. */}
      {!recurring.isLoading && recurring.rows.length > 0 && (
        <Paper className="p-main">
          <div className="flex items-baseline justify-between gap-main pb-sm">
            <Typography component="h3">Recorrência, mês a mês</Typography>
            <Typography className="text-sm text-lightBlack">
              saldo do período: {formatSignedMoney(recurring.totals.netMrr)}
            </Typography>
          </div>
          {/* Cobertura própria da série, senão os asteriscos por linha ficam sem frase que os explique. */}
          {recurring.hasLoaded && (
            <MrrCoverageNote
              coverage={recurring.coverage}
              instant="opening"
              snapshot={recurring.snapshot}
            />
          )}
          {/* Mensal porque é o default do hook: MRR é uma medida por mês, e balde diário não diz nada. */}
          <RecurringRevenueTable rows={recurring.rows} isMonthly />
        </Paper>
      )}

      {/* Mesmo motivo de TopOffers: a resposta anterior sobrevive ao refetch. */}
      {isFunnelReady && (
        <>
          <StageBreakdown stages={funnel.data.stages} />
          <OfferBreakdown rows={funnel.data.byOffer} />
          <PaymentMethodFunnelBreakdown rows={funnel.data.byPaymentMethod} />
          <OriginBreakdown data={funnel.data} />
        </>
      )}
    </div>
  );
}

/* Peças de apoio                                                             */

/** ProfitIndicator já trata divisão por zero; o que ele não sabe é se a amostra anterior tinha volume. */
function Delta({
  current,
  previous,
  sample,
  isAvailable,
  invert = false,
}: {
  current: number;
  previous: number;
  sample: number;
  isAvailable: boolean;
  invert?: boolean;
}) {
  if (!isAvailable) return null;

  if (sample < MIN_SAMPLE_FOR_DELTA)
    return (
      <div
        className="flex justify-center items-center rounded-[12px] h-[24px] px-[8px] bg-[#f3f4f6] text-[#6b7280]"
        title={`O período anterior teve menos de ${MIN_SAMPLE_FOR_DELTA} registros. Uma variação percentual sobre essa base oscila demais para significar alguma coisa.`}
      >
        <Typography className="text-xs">amostra baixa</Typography>
      </div>
    );

  return (
    <ProfitIndicator
      current={current}
      previous={previous}
      invertLogic={invert}
    />
  );
}

/** SalesCharts busca o mesmo sem expor os totais, e ele está em produção: daí a busca repetida aqui. */
function useSalesSummaryPair(filters: SalesSummaryFilters) {
  const toast = useToastContext();
  const [data, setData] = useState<SalesSummary>(emptySalesSummary);
  /** Null distingue "não deu para carregar" de "carregou e estava vazio" — só o segundo vira badge. */
  const [previous, setPrevious] = useState<SalesSummary | null>(
    emptySalesSummary,
  );
  const [isLoading, setIsLoading] = useState(false);
  /**
   * Falso quando o período atual não carregou: virando zero, o comparativo com o anterior íntegro pinta
   * queda enorme em vermelho onde houve requisição que não voltou.
   */
  const [hasCurrent, setHasCurrent] = useState(true);

  useEffect(() => {
    // Protege contra resposta atrasada: trocar de período dispara um par novo com o antigo ainda em voo.
    let active = true;

    if (!filters.botConfigIds?.length) {
      setData(emptySalesSummary);
      setPrevious(emptySalesSummary);
      setHasCurrent(true);
      setIsLoading(false);
      return () => {
        active = false;
      };
    }

    const parsed = parseFilters(filters);
    setIsLoading(true);

    // Settled, não all: a comparação falhar não pode descartar um período atual que voltou íntegro.
    Promise.allSettled([
      findSalesSummary(parsed),
      findSalesSummary({
        ...parsed,
        startDate: parsed.previousPeriodStartDate,
        endDate: parsed.previousPeriodEndDate,
      }),
    ])
      .then(([current, prior]) => {
        if (!active) return;

        setHasCurrent(current.status === 'fulfilled');
        setData(
          current.status === 'fulfilled'
            ? current.value.data ?? emptySalesSummary
            : emptySalesSummary,
        );
        setPrevious(
          prior.status === 'fulfilled'
            ? prior.value.data ?? emptySalesSummary
            : null,
        );

        if (current.status === 'rejected')
          toast.handleError(
            current.reason,
            'Não foi possível carregar os registros',
          );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
    // eslint-disable-next-line
  }, [filters]);

  return { data, previous, hasCurrent, isLoading };
}

const emptySalesSummary: SalesSummary = {
  salesTotalCount: '0',
  salesTotalValue: '0',
  refundTotalCount: '0',
  refundTotalValue: '0',
  sales: [],
};
